import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  AlertTriangle,
  Copy,
  Check,
  EyeOff,
  Eye,
  CheckCircle2,
  X,
  KeyRound,
  Server,
  Hash,
  Shield,
  ToggleLeft,
  User,
  Lock,
  Info,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { FormLabelIcon, FormGradientDefs } from "../../../molecules/Dashboard/Member/memberFormStyles";
import { C2CSession, C2CFtps } from "../../../../Redux/ApiCalls/Dashboard/C2CAPI";

interface CredentialsModalProps {
  pendingCreds: {
    session: C2CSession;
    ftps: C2CFtps;
    isNew: boolean;
  };
  onClose: () => void;
  copyToClipboard: (value: string, key: string) => void;
  copied: string | null;
}

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 24px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.22);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  line-height: 1.5;
  flex-shrink: 0;
`;

const CredList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CredItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.18);
    box-shadow: 0 4px 14px rgba(201, 77, 170, 0.06);
  }
`;

const CredLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CredValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CredValue = styled.span<{ $mono?: boolean }>`
  flex: 1;
  min-width: 0;
  font-family: ${(p) => (p.$mono ? '"Courier New", monospace' : "Urbanist, sans-serif")};
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  word-break: break-all;
`;

const CredHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.42);
`;

const IconBtn = styled.button<{ $success?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  color: ${(p) => (p.$success ? "#16a34a" : "rgba(23, 23, 23, 0.55)")};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: ${(p) => (p.$success ? "#16a34a" : "#c94daa")};
  }
`;

const GuideNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(206, 86, 176, 0.06) 100%);
  border: 1px solid rgba(201, 77, 170, 0.12);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.68);
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px;
  flex-shrink: 0;
`;

const CRED_FIELDS = [
  { key: "host", label: "Host / Server", icon: Server, setting: "FTP server / host" },
  { key: "port", label: "Port", icon: Hash, setting: "Port number" },
  { key: "tls", label: "TLS Mode", icon: Shield, setting: "FTPS Explicit (TLS required)" },
  { key: "passive_mode", label: "Passive Mode", icon: ToggleLeft, setting: "Enable passive mode" },
  { key: "username", label: "Username", icon: User, setting: "FTP username" },
] as const;

export const CredentialsModal: React.FC<CredentialsModalProps> = ({
  pendingCreds,
  onClose,
  copyToClipboard,
  copied,
}) => {
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleOutsideClick = () => {
    if (!pendingCreds.isNew) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (pendingCreds.isNew && e.key === "Escape") {
      e.stopPropagation();
    } else if (!pendingCreds.isNew && e.key === "Escape") {
      onClose();
    }
  };

  const getValue = (key: (typeof CRED_FIELDS)[number]["key"]): string => {
    const { ftps } = pendingCreds;
    switch (key) {
      case "host":
        return ftps.host;
      case "port":
        return String(ftps.port);
      case "tls":
        return ftps.tls;
      case "passive_mode":
        return ftps.passive_mode ? "ON" : "OFF";
      case "username":
        return ftps.username;
      default:
        return "";
    }
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay
        id="c2c-credentials-modal-overlay"
        onClick={handleOutsideClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        style={{ outline: "none" }}
      >
        <ModalShell
          style={{ maxWidth: 580 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <KeyRound size={11} />
                  FTPS Credentials
                </HeroBadge>
                <ModalTitle>Camera Connection Details</ModalTitle>
                <ModalSubtitle>
                  {pendingCreds.session.album.name} / {pendingCreds.session.folder.name}
                </ModalSubtitle>
              </HeroText>
              {!pendingCreds.isNew && (
                <CloseBtn
                  id="c2c-credentials-close"
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X size={18} />
                </CloseBtn>
              )}
            </HeroRow>
          </ModalHero>

          {pendingCreds.isNew && (
            <WarningBanner style={{ marginTop: 16 }}>
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              This password will not be shown again. Copy it before closing.
            </WarningBanner>
          )}

          <ModalBody>
            <CredList>
              {CRED_FIELDS.map(({ key, label, icon, setting }) => {
                const value = getValue(key);
                return (
                  <CredItem key={key}>
                    <CredLabel>
                      <FormLabelIcon icon={icon} />
                      {label}
                    </CredLabel>
                    <CredValueRow>
                      <CredValue $mono>{value}</CredValue>
                      <IconBtn
                        id={`c2c-copy-${key}`}
                        type="button"
                        $success={copied === key}
                        onClick={() => copyToClipboard(value, key)}
                        title="Copy"
                      >
                        {copied === key ? <Check size={15} /> : <Copy size={15} />}
                      </IconBtn>
                    </CredValueRow>
                    <CredHint>{setting}</CredHint>
                  </CredItem>
                );
              })}

              <CredItem>
                <CredLabel>
                  <FormLabelIcon icon={Lock} />
                  Password
                </CredLabel>
                <CredValueRow>
                  <CredValue
                    $mono
                    style={{ letterSpacing: passwordVisible ? 0 : 2 }}
                  >
                    {passwordVisible
                      ? pendingCreds.ftps.password || "—"
                      : "•".repeat(pendingCreds.ftps.password?.length || 0)}
                  </CredValue>
                  <IconBtn
                    id="c2c-toggle-password"
                    type="button"
                    onClick={() => setPasswordVisible((v) => !v)}
                    title={passwordVisible ? "Hide" : "Show"}
                  >
                    {passwordVisible ? <EyeOff size={15} /> : <Eye size={15} />}
                  </IconBtn>
                  <IconBtn
                    id="c2c-copy-password"
                    type="button"
                    $success={copied === "password"}
                    onClick={() =>
                      copyToClipboard(pendingCreds.ftps.password || "", "password")
                    }
                    title="Copy password"
                  >
                    {copied === "password" ? <Check size={15} /> : <Copy size={15} />}
                  </IconBtn>
                </CredValueRow>
                <CredHint>FTP password (10 chars)</CredHint>
              </CredItem>
            </CredList>

            <GuideNote>
              <Info size={16} style={{ flexShrink: 0, color: "#c94daa", marginTop: 1 }} />
              <span>
                <strong>FileZilla:</strong> Protocol = FTPS – Explicit · Encryption = Require
                explicit FTP over TLS · Passive Mode = ON
              </span>
            </GuideNote>
          </ModalBody>

          <ModalFooter>
            <PrimaryBtn
              id="c2c-credentials-saved-btn"
              type="button"
              onClick={onClose}
            >
              <CheckCircle2 size={16} />
              I&apos;ve saved these credentials — Close
            </PrimaryBtn>
          </ModalFooter>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};
